import { z } from 'zod';
import { ProjectRoleSchema, ProjectTypeSchema, TASK_STATUSES } from '../enums.js';
import { IdSchema, IsoDateSchema, IsoDateTimeSchema } from './common.js';
export const ProjectStatusSchema = z.enum(['active', 'archived']);
/** projects/{projectId} */
export const ProjectSchema = z.object({
    id: IdSchema,
    orgId: IdSchema,
    name: z.string().min(1).max(200),
    /** What kind of engagement this project is — drives which categories the TDD questionnaire
     * asks about (see questionnairePrompt in apps/api/src/llm/prompts.ts). Fixed at creation. */
    projectType: ProjectTypeSchema,
    /** Required when projectType is 'other' — the user's own name for the engagement, used in
     * place of the generic "Other" label wherever the project type is shown or described to the
     * questionnaire LLM. Unset for every other project type. */
    otherProjectTypeLabel: z.string().min(1).max(100).optional(),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema.nullable().default(null),
    leadUid: IdSchema,
    ownerUid: IdSchema,
    status: ProjectStatusSchema.default('active'),
    memberUids: z.array(IdSchema).default([]),
    /** The subset of memberUids whose explicit project role is `viewer` — denormalised (like
     * memberUids) so security rules can hide the activity feed from viewers without a per-entry
     * membership lookup, and so a viewer's client knows to skip it. */
    viewerUids: z.array(IdSchema).default([]),
    /** When on, viewers may also comment on tasks (they still can't edit anything). */
    viewerCanComment: z.boolean().default(false),
    counters: z
        .object({
        byStatus: z
            .record(z.enum(TASK_STATUSES), z.number().int().nonnegative())
            .default(Object.fromEntries(TASK_STATUSES.map((s) => [s, 0]))),
    })
        .default({}),
    createdAt: IsoDateTimeSchema,
    createdByUid: IdSchema,
});
/** projects/{projectId}/members/{uid} */
export const ProjectMemberSchema = z.object({
    uid: IdSchema,
    role: ProjectRoleSchema,
    addedByUid: IdSchema,
    addedAt: IsoDateTimeSchema,
});
export const CreateProjectInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    projectType: ProjectTypeSchema,
    otherProjectTypeLabel: z.string().min(1).max(100).optional(),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema.nullable().optional(),
    leadUid: IdSchema,
})
    .refine((input) => input.projectType !== 'other' || !!input.otherProjectTypeLabel?.trim(), {
    message: 'otherProjectTypeLabel is required when projectType is "other"',
    path: ['otherProjectTypeLabel'],
});
export const UpdateProjectInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    startDate: IsoDateSchema.optional(),
    endDate: IsoDateSchema.nullable().optional(),
    /** Owner-only (project.leads.manage) — the new lead is promoted to `lead` if below it. */
    leadUid: IdSchema.optional(),
    /** Owner-only (project.archive). */
    status: ProjectStatusSchema.optional(),
    viewerCanComment: z.boolean().optional(),
});
export const AddProjectMemberInputSchema = z.object({
    email: z.string().email(),
    role: ProjectRoleSchema,
});
export const ChangeProjectMemberRoleInputSchema = z.object({ role: ProjectRoleSchema });
//# sourceMappingURL=project.js.map